import React from 'react';
import { LayoverStopsData } from './types';
import { TimerIcon } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import Image from '@/lib/app-image';
import { getAirlineIconUrl } from '@/lib/airlineIcon';
import { useSelector } from 'react-redux';

// Helper function to safely format dates - moved outside component to be accessible
const safeFormatDate = (dateString: string, formatString: string): string => {
  try {
    if (!dateString) return '';

    const date = parseISO(dateString);
    if (!isValid(date)) return '';

    return format(date, formatString);
  } catch (error) {
    console.warn('Date formatting error:', error);
    return '';
  }
};

function FlightInfo({
  layoverStopsData,
  layoverData,
}: {
  layoverStopsData: LayoverStopsData[];
  layoverData: any;
}) {
  const stateData = useSelector((state: any) => state.flightSearch);

  return (
    <div className={`mt-2 rounded-xl border p-2 sm:mt-3 lg:mt-4`}>
      {layoverStopsData?.map((stop, index) => (
        <div key={index}>
          {/* Trip Header */}
          {((stateData.tripType === 'INRT' &&
            stop.tripIndex === 0 &&
            layoverStopsData.findIndex((data: any) => data.tripIndex === 0) ==
              index) ||
            (stateData.tripType === 'INRT' &&
              stop.tripIndex === 1 &&
              layoverStopsData.findIndex((data: any) => data.tripIndex === 1) ==
                index)) && (
            <div className="mb-2 flex items-center sm:mb-3 lg:mb-4">
              <h4 className="rounded-full bg-light px-3 py-1 text-xs font-bold text-zinc-800 sm:px-4 sm:py-1.5 sm:text-sm">
                {stop.tripIndex === 0 ? 'Departure' : 'Return'} in{' '}
                {stop.tripIndex === 0
                  ? stateData.origin_city + ' - ' + stateData.destination_city
                  : stateData.destination_city + ' - ' + stateData.origin_city}
              </h4>
            </div>
          )}

          {/* Flight Stop Item */}
          <StopItem {...stop} />

          {/* Layover Info */}
          {layoverData.map((data: any, i: number) =>
            data?.city?.toLowerCase() === stop?.destination?.toLowerCase() &&
            data?.tripIndex === stop?.tripIndex ? (
              <div
                className="my-2 flex items-center justify-center sm:my-3"
                key={i}
              >
                <div className="flex w-[90%] items-center justify-center rounded-xl bg-light p-1.5 sm:w-[85%] sm:p-2 lg:w-[80%]">
                  <h4 className="text-xs font-bold text-zinc-800 sm:text-sm">
                    {data.layoverTime && !data.layoverTime.includes('NaN')
                      ? data.layoverTime
                      : '00'}{' '}
                    Layover at {data.city}
                  </h4>
                </div>
              </div>
            ) : null,
          )}
        </div>
      ))}
    </div>
  );
}

const StopItem = (props?: LayoverStopsData) => {
  return (
    <div className="flex flex-col gap-4 p-2 sm:flex-row sm:gap-6 sm:p-3 lg:gap-8 lg:p-4">
      {/* Airline Info - Mobile */}
      <div className="flex items-center gap-3 sm:hidden">
        {props?.airline_code && (
          <Image
            src={getAirlineIconUrl(props?.airline_logo)}
            crossOrigin="anonymous"
            className="h-8 w-8"
            alt="logo"
          />
        )}
        <div>
          <h4 className="text-sm font-semibold text-zinc-800">
            {props?.airline_name}
          </h4>
          <h4 className="text-xs text-zinc-500">{props?.flight_number}</h4>
        </div>
      </div>

      {/* Desktop/Tablet Layout */}
      <div className="hidden flex-col items-center sm:flex sm:basis-1/6">
        {props?.airline_code && (
          <Image
            src={getAirlineIconUrl(props?.airline_code)}
            crossOrigin="anonymous"
            className="h-8 w-8 lg:h-8 lg:w-8"
            alt="logo"
          />
        )}
        <div className="mt-2 text-center">
          <h4 className="text-sm font-semibold text-zinc-800 lg:text-base">
            {props?.airline_name}
          </h4>
          <h4 className="text-xs text-zinc-500">{props?.flight_number}</h4>
        </div>
      </div>

      {/* Flight Details */}
      <div className="flex flex-1 items-center justify-between sm:items-start sm:justify-around">
        {/* Departure Info */}
        <div className="flex-1 flex-col gap-1 sm:gap-2">
          <div>
            <h4 className="text-sm font-bold text-zinc-800 sm:text-base">
              {props?.departure_time}
            </h4>
            <h4 className="text-xs text-text_secondary">
              {safeFormatDate(props?.departure_date || '', 'eee, d MMM-yy')}
            </h4>
          </div>
          <div className="mt-1 sm:mt-2">
            <h4 className="text-xs font-bold text-zinc-800 sm:text-sm">
              {props?.departure_city_name}
            </h4>
            <h4 className="text-xs text-text_secondary">
              {props?.departure_airport_name}
              {props?.departure_country_name &&
                `, ${props?.departure_country_name}`}
            </h4>
            <h4 className="text-xs text-text_secondary">
              {props?.departure_terminal}
            </h4>
          </div>
        </div>

        {/* Duration */}
        <div className="flex-1 flex-col items-center justify-center px-2 sm:px-4">
          <TimerIcon className="ml-4 h-4 w-4 text-orange-400 sm:h-5 sm:w-5" />
          <div className="mt-1 text-xs sm:text-sm">{props?.duration}</div>
        </div>

        {/* Arrival Info */}
        <div className="flex-1 flex-col gap-1 text-right sm:gap-2 sm:text-left">
          <div>
            <h4 className="text-sm font-bold text-zinc-800 sm:text-base">
              {props?.arrival_time}
            </h4>
            <h4 className="text-xs text-text_secondary">
              {safeFormatDate(props?.arrival_date || '', 'eee, d MMM-yy')}
            </h4>
          </div>
          <div className="mt-1 sm:mt-2">
            <h4 className="text-xs font-bold text-zinc-800 sm:text-sm">
              {props?.arrival_city_name}
            </h4>
            <h4 className="text-xs text-text_secondary">
              {props?.arrival_airport_name}
              {props?.arrival_country_name &&
                `, ${props?.arrival_country_name}`}
            </h4>
            <h4 className="text-xs text-text_secondary">
              {props?.arrival_terminal}
            </h4>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightInfo;
